import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Collapse,
  Button,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { ArrowBackIosNew as ArrowBackIcon, ArrowForwardIos as ArrowForwardIcon } from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import MindmapViewer from '../Mindmap/MindmapViewer';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface InformationPanelPermanentProps {
  mindmapMarkdown?: string | null;
  mindmapMetadata?: any;
  citations?: any[];
  sources?: any[];
  onCitationClick?: (citation: any) => void;
  selectedCitationIndex?: number;
  isOpen: boolean;
  onToggle: () => void;
}

const InformationPanelPermanent: React.FC<InformationPanelPermanentProps> = ({
  mindmapMarkdown,
  mindmapMetadata,
  citations = [],
  onCitationClick,
  selectedCitationIndex,
  isOpen,
  onToggle,
}) => {
  const [mindmapExpanded, setMindmapExpanded] = useState(true);
  const [expandedCitationIndex, setExpandedCitationIndex] = useState<number | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);
  const [previewScale, setPreviewScale] = useState(1.0);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const [loadedPdfs, setLoadedPdfs] = useState<Map<string, { numPages: number; pdfData: any }>>(new Map());
  const documentRefs = useRef<Map<string, any>>(new Map());

  // Auto-expand selected citation - respond to prop changes
  useEffect(() => {
    console.log('📍 selectedCitationIndex changed:', selectedCitationIndex);
    if (selectedCitationIndex !== undefined && selectedCitationIndex !== null) {
      console.log('✅ Expanding citation at index:', selectedCitationIndex);
      setExpandedCitationIndex(selectedCitationIndex);
    }
  }, [selectedCitationIndex]);

  // Get unique document IDs from citations
  const uniqueDocumentIds = useMemo(() => {
    const ids = new Set<string>();
    citations.forEach(citation => {
      if (citation.document_id) {
        ids.add(citation.document_id);
      }
    });
    return Array.from(ids);
  }, [citations]);

  // Memoize PDF URLs to prevent unnecessary reloads
  const pdfUrls = useMemo(() => {
    const urls = new Map<string, any>();
    const token = localStorage.getItem('token');
    const organization = localStorage.getItem('organization');
    let orgId = '';

    if (organization) {
      try {
        const org = JSON.parse(organization);
        orgId = org.id;
      } catch (e) {
        console.error('Failed to parse organization:', e);
      }
    }

    uniqueDocumentIds.forEach(docId => {
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      };
      if (orgId) {
        headers['X-Organization-ID'] = orgId;
      }

      urls.set(docId, {
        url: `${process.env.REACT_APP_API_BASE_URL}/api/v1/documents/${docId}/download`,
        httpHeaders: headers
      });
    });

    return urls;
  }, [uniqueDocumentIds]);

  // Get PDF URL helper
  const getPdfUrl = (documentId: string) => {
    return pdfUrls.get(documentId) || {
      url: `${process.env.REACT_APP_API_BASE_URL}/api/v1/documents/${documentId}/download`,
      httpHeaders: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      }
    };
  };

  // Preload all unique PDFs when citations change
  useEffect(() => {
    uniqueDocumentIds.forEach(docId => {
      if (!loadedPdfs.has(docId)) {
        console.log(`📥 Preloading PDF: ${docId}`);
      }
    });
  }, [uniqueDocumentIds, loadedPdfs]);

  const handleCitationClick = (citation: any, index: number) => {
    // Toggle expansion
    setExpandedCitationIndex(expandedCitationIndex === index ? null : index);

    // Notify parent
    if (onCitationClick) {
      onCitationClick(citation);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start', // 👈 aligns items to the left
            gap: 1.5,                     // 👈 adds spacing between icon and text
            width: '100%',
          }}
        >
          <Tooltip title={isOpen ? "Hide Information Panel" : "Show Information Panel"}>
            <IconButton onClick={onToggle} size="small">
              {isOpen ? <ArrowForwardIcon fontSize="small" /> : <ArrowBackIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ fontWeight: 600, ml: 1 }}>
            Information panel
          </Typography>
        </Box>
      </Box>

      {isOpen ?
        <>
          {/* Hidden PDF loaders - Load each unique PDF once and store reference */}
          <Box sx={{ display: 'none' }}>
            {uniqueDocumentIds.map(docId => (
              <Document
                key={docId}
                file={getPdfUrl(docId)}
                onLoadSuccess={(pdf) => {
                  documentRefs.current.set(docId, pdf);
                  setLoadedPdfs(prev => new Map(prev).set(docId, { numPages: pdf.numPages, pdfData: pdf }));
                  console.log(`✅ PDF preloaded: ${docId}, ${pdf.numPages} pages`);
                }}
              >
                <Page pageNumber={1} />
              </Document>
            ))}
          </Box>

          {/* Content - Kotaemon-style: Mindmap at top, then citations with PDF and text */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {/* Mindmap Section - Always at top */}
            {mindmapMarkdown && (
              <Box sx={{ mb: 2 }}>
                <Button
                  fullWidth
                  onClick={() => setMindmapExpanded(!mindmapExpanded)}
                  endIcon={mindmapExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    color: 'text.primary',
                    fontWeight: 600,
                    mb: 1,
                    bgcolor: 'primary.50',
                    '&:hover': {
                      bgcolor: 'primary.100',
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    🗺️ Knowledge Map
                  </Typography>
                </Button>
                <Collapse in={mindmapExpanded}>
                  <Box sx={{ bgcolor: 'background.default', borderRadius: 1, p: 2, border: 1, borderColor: 'divider' }}>
                    <MindmapViewer
                      markdown={mindmapMarkdown}
                      height={300}
                      metadata={mindmapMetadata}
                    />
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* Citations List - Optimized: Show text only, no PDF previews to prevent multiple loads */}
            {citations && citations.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                  📚 Citations ({citations.length})
                </Typography>
                <List dense sx={{ p: 0 }}>
                  {citations.map((citation, index) => (
                    <Box key={index} sx={{ mb: 1.5 }}>
                      <ListItemButton
                        onClick={() => handleCitationClick(citation, index)}
                        sx={{
                          borderRadius: 1,
                          border: 1,
                          borderColor: expandedCitationIndex === index ? 'primary.main' : 'divider',
                          bgcolor: expandedCitationIndex === index ? 'primary.50' : 'background.paper',
                          p: 1.5,
                          '&:hover': {
                            bgcolor: expandedCitationIndex === index ? 'primary.100' : 'action.hover',
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {expandedCitationIndex === index ? '▼' : '▶'} [{citation.document_name || 'Document'}, Page {citation.page}]
                              </Typography>
                              <Chip
                                label={citation.score?.toFixed(2) || 'N/A'}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          }
                        />
                      </ListItemButton>

                      {/* Kotaemon-style: Show text content and preview button only */}
                      <Collapse in={expandedCitationIndex === index}>
                        <Box sx={{
                          p: 2,
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          mt: 1,
                          border: 1,
                          borderColor: 'primary.main',
                          borderTop: 0,
                          borderTopLeftRadius: 0,
                          borderTopRightRadius: 0,
                        }}>
                          {/* PDF Preview Button - Opens dialog to prevent multiple downloads */}
                          {citation.document_id && (
                            <Box sx={{ mb: 2 }}>
                              <Button
                                variant="contained"
                                fullWidth
                                startIcon={<FullscreenIcon />}
                                onClick={() => {
                                  const docId = citation.document_id;
                                  setPreviewDocumentId(docId);
                                  setPreviewPage(citation.page || 1);
                                  // Set numPages from preloaded data if available
                                  const preloadedData = loadedPdfs.get(docId);
                                  if (preloadedData) {
                                    setPreviewNumPages(preloadedData.numPages);
                                    console.log(`🔄 Using preloaded PDF: ${docId}, ${preloadedData.numPages} pages`);
                                  }
                                  setPdfPreviewOpen(true);
                                }}
                                sx={{
                                  textTransform: 'none',
                                  py: 1.5,
                                }}
                              >
                                📄 View Page {citation.page} in PDF
                              </Button>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                                {citation.document_name || 'Document'}
                              </Typography>
                            </Box>
                          )}

                          {/* Additional Context with Highlighted Text */}
                          {citation.matched_text && (
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'text.primary' }}>
                                📝 Context:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  p: 1.5,
                                  bgcolor: 'background.paper',
                                  borderRadius: 1,
                                  fontSize: '0.85rem',
                                  lineHeight: 1.6,
                                  maxHeight: 300,
                                  overflow: 'auto',
                                  border: 1,
                                  borderColor: 'divider',
                                }}
                              // COMMENTED OUT: Citation highlighting functionality
                              // dangerouslySetInnerHTML={{
                              //   __html: (() => {
                              //     // Use matched_text as both the context AND the text to highlight
                              //     // citation_text is from the response and won't match PDF content
                              //     const contextText = citation.matched_text || '';
                              //     const citationText = citation.citation_text || citation.text || '';
                              //     
                              //     console.log('🔍 Highlighting attempt for citation index', index, ':', {
                              //       citationText: citationText.substring(0, 100),
                              //       contextTextLength: contextText.length,
                              //       contextPreview: contextText.substring(0, 200),
                              //       hasContext: !!contextText,
                              //       hasCitation: !!citationText,
                              //       citationIndex: index
                              //     });
                              //     
                              //     console.log('📝 Full citation object for index', index, ':', citation);
                              //     
                              //     if (!contextText) {
                              //       console.log('⚠️ Missing context text for index', index);
                              //       return citationText || '';
                              //     }
                              //     
                              //     // Highlight first 100 chars of matched_text (PDF content)
                              //     const textToHighlight = contextText.substring(0, 100).trim();
                              //     
                              //     if (!textToHighlight) {
                              //       return contextText;
                              //     }
                              //     
                              //     console.log('✅ Highlighting first 100 chars of PDF content');
                              //     
                              //     // Escape special regex characters
                              //     const escapedText = textToHighlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                              //     const flexiblePattern = escapedText.replace(/\s+/g, '\\s+');
                              //     
                              //     const regex = new RegExp(`(${flexiblePattern})`, 'i');
                              //     
                              //     const highlighted = contextText.replace(
                              //       regex,
                              //       '<mark style="background-color: rgba(255, 235, 59, 0.6); padding: 2px 4px; border-radius: 2px; font-weight: 600;">$1</mark>'
                              //     );
                              //     
                              //     console.log('✅ Highlighting applied, result length:', highlighted.length);
                              //     console.log('🎨 Highlighted HTML preview:', highlighted.substring(0, 500));
                              //     return highlighted;
                              //     
                              //     // Extract numbers and key terms for fuzzy matching
                              //     const numbersMatch = citationText.match(/[\d,]+\.?\d*/g) || [];
                              //     const percentages = citationText.match(/\d+\.?\d*%/g) || [];
                              //     const allMatches = [...numbersMatch, ...percentages];
                              //     
                              //     // Try to find and highlight numbers/percentages
                              //     if (allMatches.length > 0) {
                              //       let highlightedText = contextText;
                              //       
                              //       // Sort by length (longest first) to avoid partial matches
                              //       allMatches.sort((a, b) => b.length - a.length);
                              //       
                              //       for (const match of allMatches) {
                              //         const escapedMatch = match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                              //         const regex = new RegExp(`(${escapedMatch})`, 'g');
                              //         
                              //         highlightedText = highlightedText.replace(
                              //           regex,
                              //           '<mark style="background-color: rgba(255, 235, 59, 0.6); padding: 2px 4px; border-radius: 2px; font-weight: 600;">$1</mark>'
                              //         );
                              //       }
                              //       
                              //       // If we highlighted something, return it
                              //       if (highlightedText !== contextText) {
                              //         return highlightedText;
                              //       }
                              //     }
                              //     
                              //     // If exact match fails, try fuzzy matching by splitting into words
                              //     const words = normalizedCitation.split(' ').filter((w: string) => w.length > 2);
                              //     if (words.length > 0) {
                              //       // Highlight the first significant chunk of words found
                              //       const escapedWords = words.slice(0, Math.min(5, words.length)).map((w: string) =>
                              //         w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                              //       ).join('\\s+');
                              //       const regex = new RegExp(`(${escapedWords})`, 'gi');
                              //       
                              //       return contextText.replace(
                              //         regex,
                              //         '<mark style="background-color: rgba(255, 235, 59, 0.6); padding: 2px 4px; border-radius: 2px; font-weight: 600;">$1</mark>'
                              //       );
                              //     }
                              //     
                              //     // If all else fails, just return the context without highlighting
                              //     return contextText;
                              //   })()
                              // }}
                              >
                                {citation.matched_text || citation.text || ''}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </List>
              </Box>
            )}

            {/* Empty State */}
            {!mindmapMarkdown && (!citations || citations.length === 0) && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                  💬
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  No information available yet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ask a question to see citations and knowledge map
                </Typography>
              </Box>
            )}
          </Box>

          {/* Hidden PDF preview Documents - Keep mounted to avoid re-downloading */}
          <Box sx={{ display: pdfPreviewOpen ? 'none' : 'none' }}>
            {uniqueDocumentIds.map(docId => {
              const pdfData = loadedPdfs.get(docId);
              return pdfData && previewDocumentId === docId ? (
                <Document
                  key={`preview-${docId}`}
                  file={getPdfUrl(docId)}
                  onLoadSuccess={({ numPages }) => {
                    if (!previewNumPages) {
                      setPreviewNumPages(numPages);
                    }
                  }}
                >
                  <Page pageNumber={previewPage} scale={previewScale} />
                </Document>
              ) : null;
            })}
          </Box>

          {/* PDF Preview Dialog */}
          <Dialog
            open={pdfPreviewOpen}
            onClose={() => setPdfPreviewOpen(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: { height: '90vh' }
            }}
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">PDF Preview</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <IconButton size="small" onClick={() => setPreviewScale(s => Math.max(0.5, s - 0.1))}>
                    <ZoomOutIcon />
                  </IconButton>
                  <Chip label={`${Math.round(previewScale * 100)}%`} size="small" />
                  <IconButton size="small" onClick={() => setPreviewScale(s => Math.min(2.0, s + 0.1))}>
                    <ZoomInIcon />
                  </IconButton>
                  <IconButton onClick={() => setPdfPreviewOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Button
                  size="small"
                  onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
                  disabled={previewPage <= 1}
                >
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {previewPage} {previewNumPages ? `of ${previewNumPages}` : ''}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setPreviewPage(p => Math.min(previewNumPages || p, p + 1))}
                  disabled={previewPage >= (previewNumPages || 1)}
                >
                  Next
                </Button>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#525659', display: 'flex', justifyContent: 'center', p: 2 }}>
                {previewDocumentId && loadedPdfs.has(previewDocumentId) && (
                  <Document
                    key={`dialog-${previewDocumentId}`}
                    file={getPdfUrl(previewDocumentId)}
                    onLoadSuccess={({ numPages }) => {
                      console.log(`♻️ Reusing cached PDF in dialog: ${previewDocumentId}`);
                      if (!previewNumPages) {
                        setPreviewNumPages(numPages);
                      }
                    }}
                    loading={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                        <CircularProgress sx={{ color: 'white' }} />
                        <Typography sx={{ color: 'white' }}>Loading PDF...</Typography>
                      </Box>
                    }
                  >
                    <Page
                      pageNumber={previewPage}
                      scale={previewScale}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                    />
                  </Document>
                )}
              </Box>
            </DialogContent>
          </Dialog>

          {/* Image Enlarge Dialog */}
          {enlargedImage && (
            <Dialog
              open={!!enlargedImage}
              onClose={() => setEnlargedImage(null)}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: { bgcolor: 'rgba(0, 0, 0, 0.9)' }
              }}
            >
              <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <IconButton
                  onClick={() => setEnlargedImage(null)}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <img
                  src={enlargedImage}
                  alt="Enlarged view"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '90vh',
                    objectFit: 'contain',
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
        </>
        : <></>}
    </Box>
  );
};

export default InformationPanelPermanent;
